import React, { useState } from 'react'
import { Check } from 'lucide-react'

export interface ColorSwatchPickerProps {
    colors: string[]
    value?: string
    defaultValue?: string
    onChange?: (color: string) => void
    shape?: 'circle' | 'square'
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    disabled?: boolean
    style?: React.CSSProperties
}

type SwatchSize = NonNullable<ColorSwatchPickerProps['size']>

const sizeMap: Record<SwatchSize, number> = { xs: 16, sm: 20, md: 28, lg: 36, xl: 44 }
const sizeClasses: Record<SwatchSize, string> = { xs: 'size-4', sm: 'size-5', md: 'size-7', lg: 'size-9', xl: 'size-11' }

export const ColorSwatchPicker = ({
    colors,
    value: controlledValue,
    defaultValue,
    onChange,
    shape = 'circle',
    size = 'md',
    disabled = false,
    style,
}: ColorSwatchPickerProps) => {
    const [internalValue, setInternalValue] = useState(defaultValue ?? '')
    const selected = controlledValue !== undefined ? controlledValue : internalValue

    const px = sizeMap[size]

    const handleSelect = (color: string) => {
        if (disabled) return
        if (controlledValue === undefined) setInternalValue(color)
        onChange?.(color)
    }

    return (
        <div role="radiogroup" className={`flex flex-wrap items-center ${px >= 28 ? 'gap-2' : 'gap-1.5'}`} style={style}>
            {colors.map((color) => {
                const isSelected = selected === color

                return (
                    <button
                        key={color}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        aria-label={color}
                        disabled={disabled}
                        onClick={() => handleSelect(color)}
                        style={{ backgroundColor: color }} /* raw swatch color — data, not theme */
                        className={`box-border flex shrink-0 items-center justify-center border-none p-0 transition-transform duration-100 ${sizeClasses[size]} ${
                            shape === 'circle' ? 'rounded-full' : 'rounded-md'
                        } ${isSelected ? 'ring-2 ring-accent ring-offset-2 ring-offset-background' : ''} ${
                            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-110'
                        }`}
                    >
                        {isSelected && <Check size={Math.round(px * 0.5)} strokeWidth={1.5} className="text-white" />}
                    </button>
                )
            })}
        </div>
    )
}
